/**
 * PNL Modal - модалка с превью PNL карточки и кнопками шаринга
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import SoundManager from '../../services/SoundManager';
import './PnlModal.css';

interface PnlData {
  telegramId: number;
  username: string;
  firstName: string;
  daysInGame: number;
  rank: string;
  vipStatus: string;
  leaderStatus: string;
  totalEarnedSperm: number;
  dailyIncomeSperm: number;
  totalEarnedUsdt: number;
  dailyIncomeUsdt: number;
  referralEarningsSperm: number;
  referralEarningsUsdt: number;
  activeReferrals: number;
  totalReferrals: number;
  availableLevel: number;
  maxLevel: number;
  referralCode: string;
}

interface PnlModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PnlModal: React.FC<PnlModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [pnlData, setPnlData] = useState<PnlData | null>(null);
  const [sharing, setSharing] = useState(false);
  const [sendingToBot, setSendingToBot] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Загружаем изображение при открытии
  useEffect(() => {
    if (isOpen) {
      loadPnlImage();
    }
    return () => {
      // Очищаем blob URL при закрытии
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [isOpen]);

  const loadPnlImage = async () => {
    try {
      setLoading(true);
      setError(null);

      // Загружаем данные и изображение параллельно
      const [dataRes, imageRes] = await Promise.all([
        api.get('/pnl/data'),
        api.get('/pnl/image', { responseType: 'blob' })
      ]);

      if (dataRes.data.success) {
        setPnlData(dataRes.data.data);
      }

      // Создаём blob URL для превью
      const blob = new Blob([imageRes.data], { type: 'image/png' });
      const url = URL.createObjectURL(blob);
      setImageUrl(url);

    } catch (err: any) {
      console.error('[PNL] Ошибка загрузки:', err);
      setError(err.response?.data?.message || t('pnl.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  // Поделиться в Stories
  const handleShareToStory = async () => {
    try {
      setSharing(true);
      setError(null);
      setSuccessMessage(null);

      // Получаем публичный URL для изображения
      const res = await api.get('/pnl/share-url');

      if (!res.data.success) {
        throw new Error(res.data.message);
      }

      const { imageUrl: publicImageUrl, referralLink } = res.data.data;

      // Используем Telegram WebApp API
      const telegram = (window as any).Telegram?.WebApp;

      if (!telegram?.shareToStory) {
        setError(t('pnl.storyNotSupported'));
        return;
      }

      // Тактильная обратная связь
      if (telegram.HapticFeedback) {
        telegram.HapticFeedback.impactOccurred('medium');
      }

      // Шарим в Stories с реферальной ссылкой в тексте
      telegram.shareToStory(publicImageUrl, {
        text: `${t('pnl.storyText')}\n\n🔗 ${referralLink}`,
        widget_link: {
          url: referralLink,
          name: t('pnl.joinButton')
        }
      });

      SoundManager.playClaimSuccess();
      setSuccessMessage(t('pnl.storyShared'));

    } catch (err: any) {
      console.error('[PNL] Ошибка шаринга в Stories:', err);
      setError(err.response?.data?.message || t('pnl.errorSharing'));

      const telegram = (window as any).Telegram?.WebApp;
      if (telegram?.HapticFeedback) {
        telegram.HapticFeedback.notificationOccurred('error');
      }
    } finally {
      setSharing(false);
    }
  };

  // Отправить в Telegram бота
  const handleSendToBot = async () => {
    try {
      setSendingToBot(true);
      setError(null);
      setSuccessMessage(null);

      const res = await api.post('/pnl/send-to-bot');

      if (!res.data.success) {
        throw new Error(res.data.message);
      }

      const telegram = (window as any).Telegram?.WebApp;
      if (telegram?.HapticFeedback) {
        telegram.HapticFeedback.notificationOccurred('success');
      }

      SoundManager.playClaimSuccess();
      setSuccessMessage(t('pnl.sentToBot'));

    } catch (err: any) {
      console.error('[PNL] Ошибка отправки в бота:', err);
      setError(err.response?.data?.message || t('pnl.errorSendingToBot'));

      const telegram = (window as any).Telegram?.WebApp;
      if (telegram?.HapticFeedback) {
        telegram.HapticFeedback.notificationOccurred('error');
      }
    } finally {
      setSendingToBot(false);
    }
  };

  // Скачать изображение
  const handleDownload = () => {
    if (!imageUrl) return;

    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `pnl_${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    SoundManager.playButtonClick();
  };

  // Форматирование числа
  const formatNumber = (num: number, decimals: number = 0): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString('ru-RU', { maximumFractionDigits: decimals });
  };

  if (!isOpen) return null;

  return (
    <div className="pnl-modal-overlay" onClick={onClose}>
      <div className="pnl-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Заголовок */}
        <div className="pnl-modal-header">
          <h2>{t('pnl.title')}</h2>
          <button className="pnl-modal-close" onClick={() => { SoundManager.playButtonClick(); onClose(); }}>
            <span>×</span>
          </button>
        </div>

        {/* Контент */}
        <div className="pnl-modal-body">
          {loading ? (
            <div className="pnl-loading">
              <div className="pnl-spinner"></div>
              <p>{t('pnl.generating')}</p>
            </div>
          ) : error && !imageUrl ? (
            <div className="pnl-error">
              <span className="error-icon">⚠️</span>
              <p>{error}</p>
              <button className="pnl-retry-btn" onClick={loadPnlImage}>
                {t('common.retry')}
              </button>
            </div>
          ) : (
            <>
              {/* Превью изображения */}
              <div className="pnl-image-preview">
                {imageUrl && (
                  <img src={imageUrl} alt="PNL Card" className="pnl-preview-img" />
                )}
              </div>

              {/* Краткая статистика */}
              {pnlData && (
                <div className="pnl-stats-summary">
                  <div className="pnl-stat-item">
                    <span className="pnl-stat-label">{t('pnl.earnedSperm')}</span>
                    <span className="pnl-stat-value positive">+{formatNumber(pnlData.totalEarnedSperm)}</span>
                  </div>
                  <div className="pnl-stat-item">
                    <span className="pnl-stat-label">{t('pnl.earnedUsdt')}</span>
                    <span className="pnl-stat-value positive">+{formatNumber(pnlData.totalEarnedUsdt, 2)}</span>
                  </div>
                  <div className="pnl-stat-item team-stat">
                    <span className="pnl-stat-label">{t('pnl.team')}</span>
                    <span className="pnl-stat-value">{pnlData.totalReferrals}</span>
                    <span className="pnl-stat-active">({t('pnl.active')}: {pnlData.activeReferrals})</span>
                  </div>
                </div>
              )}

              {/* Сообщения */}
              {error && (
                <div className="pnl-message error">
                  <span>⚠️</span> {error}
                </div>
              )}
              {successMessage && (
                <div className="pnl-message success">
                  <span>✅</span> {successMessage}
                </div>
              )}
            </>
          )}
        </div>

        {/* Кнопки действий — только в Telegram */}
        {!loading && imageUrl && localStorage.getItem('webauth') !== 'true' && (
          <div className="pnl-modal-actions">
            <button
              className="pnl-action-btn story-btn"
              onClick={handleShareToStory}
              disabled={sharing}
            >
              {sharing ? (
                <span className="btn-loading"></span>
              ) : (
                <>
                  <span className="btn-icon">📱</span>
                  {t('pnl.shareToStory')}
                </>
              )}
            </button>

            <button
              className="pnl-action-btn bot-btn"
              onClick={handleSendToBot}
              disabled={sendingToBot}
            >
              {sendingToBot ? (
                <span className="btn-loading"></span>
              ) : (
                <>
                  <span className="btn-icon">🤖</span>
                  {t('pnl.sendToBot')}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PnlModal;
